"use client";

import dynamic from "next/dynamic";
import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";

const HalfLifeChart = dynamic(
	() => import("@/ui/components/half-life/half-life-chart").then((m) => m.HalfLifeChart),
	{ ssr: false, loading: () => <div className="h-[360px] w-full animate-pulse rounded bg-muted" /> },
);
import { HalfLifeKpiRow } from "@/ui/components/half-life/half-life-kpi-row";
import { HalfLifeParamsBar, type IntervalOption } from "@/ui/components/half-life/half-life-params-bar";
import { HalfLifePeptideSelector } from "@/ui/components/half-life/half-life-peptide-selector";
import { computeHalfLifeResult, formatDurationLabel } from "@/lib/calculator/half-life";
import { formatHalfLifeHours } from "@/lib/category/format-half-life";
import {
	getPeptideName,
	getPeptideHalfLifeSource,
	getPeptideHalfLifeSourceLanguage,
} from "@/lib/calculator/peptide-data";
import type { PeptideData } from "@/lib/calculator/types";
import type { Locale } from "@/config/locale";

interface HalfLifeClientProps {
	peptides: PeptideData[];
	initialSlug: string;
	locale: Locale;
	/** If true, the peptide switcher is hidden (spoke mode). */
	lockPeptide?: boolean;
}

function frequencyToIntervalHours(frequency: string): number {
	const f = frequency.toLowerCase();
	if (f.includes("week") || f.includes("wöch")) return 168;
	if (f.includes("2-3x week")) return 72;
	if (f.includes("2x daily") || f.includes("twice daily") || f.includes("2x täglich")) return 12;
	if (f.includes("2-3x daily")) return 8;
	if (f.includes("1-3x daily")) return 12;
	if (f.includes("daily") || f.includes("täglich")) return 24;
	if (f.includes("hour")) return 1;
	return 24;
}

export function HalfLifeCalculatorClient({
	peptides,
	initialSlug,
	locale,
	lockPeptide = false,
}: HalfLifeClientProps) {
	const t = useTranslations("halfLifeCalculator");
	const [slug, setSlug] = useState(initialSlug);
	const [prevSlug, setPrevSlug] = useState(initialSlug);

	const peptide = peptides.find((p) => p.slug === slug) ?? peptides[0];
	const halfLifeSource = peptide ? getPeptideHalfLifeSource(peptide, locale) : undefined;
	const halfLifeSourceLang = peptide ? getPeptideHalfLifeSourceLanguage(peptide, locale) : undefined;
	const halfLifeHours = peptide?.halfLifeHours ?? 24;

	const [dose, setDose] = useState(peptide?.defaultDose ?? 1);
	const [intervalHours, setIntervalHours] = useState(frequencyToIntervalHours(peptide?.frequency ?? "daily"));
	const [numDoses, setNumDoses] = useState(8);

	// Reset dose/interval to peptide defaults when the selected peptide changes.
	// Using render-time state update (recommended over useEffect + setState).
	if (slug !== prevSlug) {
		setPrevSlug(slug);
		if (peptide) {
			setDose(peptide.defaultDose);
			setIntervalHours(frequencyToIntervalHours(peptide.frequency));
		}
	}

	const result = useMemo(
		() => computeHalfLifeResult({ halfLifeHours, intervalHours, numDoses }, locale),
		[halfLifeHours, intervalHours, numDoses, locale],
	);

	const intervalOptions: IntervalOption[] = [
		{ value: 1, label: t("params.intervalOptions.hourly") },
		{ value: 6, label: t("params.intervalOptions.everySixHours") },
		{ value: 12, label: t("params.intervalOptions.twelveHourly") },
		{ value: 24, label: t("params.intervalOptions.daily") },
		{ value: 168, label: t("params.intervalOptions.weekly") },
		{ value: 336, label: t("params.intervalOptions.biWeekly") },
	];

	const kpiTiles: Parameters<typeof HalfLifeKpiRow>[0]["tiles"] = [
		{
			label: t("kpi.halfLife"),
			value: formatDurationLabel(halfLifeHours, locale),
			hint: t("kpi.halfLifeHint"),
		},
		{
			label: t("kpi.steadyState"),
			value: result.timeToSteadyStateLabel,
			hint: t("kpi.steadyStateHint"),
		},
		{
			label: t("kpi.accumulation"),
			value: `×${new Intl.NumberFormat(locale, {
				minimumFractionDigits: result.accumulationFactor < 2 ? 2 : 1,
				maximumFractionDigits: result.accumulationFactor < 2 ? 2 : 1,
			}).format(result.accumulationFactor)}`,
			hint: t("kpi.accumulationHint"),
		},
		{
			label: t("kpi.clearance"),
			value: formatDurationLabel(result.eliminationAfterLastDoseHours, locale),
			hint: t("kpi.clearanceHint"),
		},
	];

	const chartLabels = {
		percentSuffix: t("chart.yAxisLabel"),
		dayShort: t("chart.dayShort"),
		hourShort: t("chart.hourShort"),
		steadyStateLegend: t("chart.steadyStateLegend"),
		srCaption: t("chart.srCaption"),
	};

	return (
		<div className="space-y-6">
			{!lockPeptide && (
				<HalfLifePeptideSelector
					peptides={peptides}
					value={slug}
					onChange={setSlug}
					locale={locale}
					placeholder={t("selector.placeholder")}
					searchPlaceholder={t("selector.searchPlaceholder")}
					emptyLabel={t("selector.empty")}
				/>
			)}

			<HalfLifeKpiRow tiles={kpiTiles} />

			<div className="rounded-xl border border-border bg-card p-4 sm:p-6">
				<HalfLifeChart result={result} locale={locale} labels={chartLabels} />
			</div>

			<HalfLifeParamsBar
				dose={dose}
				onDoseChange={setDose}
				intervalHours={intervalHours}
				onIntervalChange={setIntervalHours}
				intervalOptions={intervalOptions}
				numDoses={numDoses}
				onNumDosesChange={setNumDoses}
				labels={{
					dose: t("params.dose"),
					interval: t("params.interval"),
					numDoses: t("params.numDoses"),
					doseSuffix: t("params.doseSuffix"),
				}}
			/>

			<p className="text-xs text-muted-foreground">
				{/* es-mx showing the es note wraps it in lang="es"; the route's own labels stay outside. */}
				{halfLifeSource && halfLifeSourceLang ? (
					<>
						{t("sourceLabel")}: <span lang={halfLifeSourceLang}>{halfLifeSource}</span>.{" "}
					</>
				) : halfLifeSource ? (
					`${t("sourceLabel")}: ${halfLifeSource}. `
				) : (
					""
				)}
				{t("disclaimer")}
				{peptide
					? ` ${getPeptideName(peptide, locale)}: ${formatHalfLifeHours(peptide.halfLifeHours, locale, peptide.halfLife)}.`
					: ""}
			</p>
		</div>
	);
}
